import "./_connect-wallet-button.scss";

import classNames from "classnames";
import {Fragment, useState} from "react";

import peraWalletManager from "../../../core/integrations/pera-wallet/PeraWalletManager";
import {AccountInformationData, AppStateAccountData} from "../../account/accountTypes";
import useDisconnectAccount from "../../account/useDisconnectAccount";
import SelectAccountModal from "../../select-account-modal/SelectAccountModal";
import {
  useWalletContext,
  useWalletContextDispatch
} from "../../util/context/WalletContext.utils";
import {
  fetchAccounts,
  isDeflyWallet,
  isMobileWallet,
  isPeraWallet,
  isWalletConnect,
  mapAccountsToAppStateAccounts
} from "../../util/walletUtils";
import ConnectWalletModal from "../modal/ConnectWalletModal";
import {AssetListItemData} from "../../../asset/util/assetModels";
import {WalletType} from "../../util/walletTypes";
import deflyWalletManager from "../../../core/integrations/defly/DeflyWalletManager";
import ContainerButton from "../../../component/button/container/ContainerButton";
import {SwapWidgetSearchParamKey} from "../../../core/widgetTypes";
import TooltipContent from "../../../component/tooltip/content/TooltipContent";
import {getParentUrlFromSearchParams} from "../../../core/widgetUtils";
import Typography from "../../../component/typography/Typography";
import walletConnectManager from "../../../core/integrations/wallet-connect/WalletConnect.manager";

interface ConnectWalletButtonProps {
  children?: React.ReactNode;
  customClassName?: string;
  isDisabled?: boolean;
}

function ConnectWalletButton({
  customClassName,
  isDisabled,
  children = "Connect to a wallet"
}: ConnectWalletButtonProps) {
  const [isConnectWalletModalOpen, setConnectWalletModalVisibility] = useState(false);
  const [isSelectAccountModalOpen, setSelectAccountModalVisibility] = useState(false);
  const walletContextDispatch = useWalletContextDispatch();
  const {walletType: selectedWalletType} = useWalletContext();

  const disconnectAccount = useDisconnectAccount();

  const searchParams = new URLSearchParams(document.location.search);
  const shouldUseParentSigner =
    searchParams.get(SwapWidgetSearchParamKey.USE_PARENT_SIGNER) === "true";
  const parentUrl = getParentUrlFromSearchParams();

  return (
    <Fragment>
      <ContainerButton
        aria-label={"Connect to a wallet"}
        onClick={openConnectWalletModal}
        customClassName={classNames("connect-wallet-button", customClassName)}
        isDisabled={isDisabled || shouldUseParentSigner}
        tooltipProps={
          shouldUseParentSigner
            ? {
                content: (
                  <TooltipContent
                    customClassName={classNames(
                      "is-flex-column",
                      "is-vertically-centered",
                      "connect-wallet-button__tooltip-content"
                    )}>
                    {parentUrl ? (
                      <Fragment>
                        <p>{"Please connect your wallet to"}</p>

                        <Typography
                          customClassName={
                            "connect-wallet-button__tooltip-content__parent-url"
                          }
                          fontWeight={"medium"}>
                          {parentUrl}
                        </Typography>
                      </Fragment>
                    ) : (
                      <p>{"Please connect your wallet to the parent app"}</p>
                    )}
                  </TooltipContent>
                ),
                placement: "top"
              }
            : undefined
        }>
        {children}
      </ContainerButton>

      {isConnectWalletModalOpen && (
        <ConnectWalletModal onClose={closeConnectWalletModal} onConnect={connectWallet} />
      )}

      {isSelectAccountModalOpen && (
        <SelectAccountModal
          isOpen={isSelectAccountModalOpen}
          onClose={closeSelectAccountModal}
        />
      )}
    </Fragment>
  );

  function openConnectWalletModal() {
    setConnectWalletModalVisibility(true);
  }

  function closeConnectWalletModal() {
    setConnectWalletModalVisibility(false);
  }

  async function connectWallet(walletType: WalletType) {
    try {
      let accounts: AccountInformationData[] = [];

      if (isMobileWallet(walletType)) {
        accounts = await connectViaWalletConnect(walletType);
      }

      const mappedAccounts = await mapAccountsToAppStateAccounts(accounts);

      setAccounts(mappedAccounts, walletType);
    } catch (error: any) {
      console.error(error);
    }
  }

  function setAccounts(
    accounts: {detail: AppStateAccountData; assets: AssetListItemData[]}[],
    walletType: WalletType
  ) {
    closeConnectWalletModal();
    walletContextDispatch({
      type: "SET_WALLET_AND_ACCOUNTS",
      accounts,
      walletType
    });

    if (accounts.length === 1) {
      walletContextDispatch({
        type: "SET_ACCOUNT",
        account: accounts[0]
      });

      if (isWalletConnect(walletType)) {
        window.location.reload();
      }
    } else {
      setSelectAccountModalVisibility(true);
    }
  }

  function closeSelectAccountModal() {
    setSelectAccountModalVisibility(false);

    if (isWalletConnect(selectedWalletType)) {
      window.location.reload();
    }
  }

  async function connectViaWalletConnect(
    walletType: Extract<WalletType, "pera-wallet" | "wallet-connect" | "defly-wallet">
  ) {
    closeConnectWalletModal();
    let accountsData: AccountInformationData[] = [];

    let accounts: string[];

    if (isPeraWallet(walletType)) {
      accounts = await peraWalletManager.connectAndSetupEventHandlers({
        onDisconnect: disconnectAccount
      });
    } else if (isDeflyWallet(walletType)) {
      accounts = await deflyWalletManager.connectAndSetupEventHandlers({
        onDisconnect: disconnectAccount
      });
    } else {
      await walletConnectManager.connect();
      accounts = walletConnectManager.getAccounts();
    }

    try {
      accountsData = await fetchAccounts(accounts);
    } catch (error) {
      if (isPeraWallet(walletType)) {
        peraWalletManager.disconnect();
      } else {
        walletConnectManager.disconnect();
      }
      throw error;
    }

    return accountsData;
  }
}

export default ConnectWalletButton;
